import React, { useState, useEffect, useContext, useRef } from "react";
import { AuthStatus } from "../../../services/cognitoService/AuthStatus";
import { ICognitoService } from "../../../services/cognitoService/ICognitoService";
import {
   CognitoAccessToken,
   CognitoUserAttribute,
   CognitoUserSession,
   ISignUpResult,
} from "amazon-cognito-identity-js";
import { getCognitoService } from "../../../services/cognitoService/getCognitoService";
import { ICognitoSignInResponse } from "../../../services/cognitoService/ICognitoSignInResponse";
import { IDonorIdEmailVerificationRequest, IDonorValidationRequest } from "../../../services/bexWISE/donorDataService/IDonorDataService";
import { getDonorDataServiceInstance } from "../../../services/bexWISE/donorDataService/getDonorDataServiceInstance";
import {IExternalDonorDTO} from "../../../types/IDonorDTO";

export interface ISessionInfo {
   username?: string;
   email?: string;
   sub?: string;
   accessToken?: string;
   refreshToken?: string;
}

export interface IAuthContext {
   sessionInfo?: ISessionInfo;
   userDonorId?: string;
   externalDonorId?: string;
   attrInfo?: CognitoUserAttribute[];
   authStatus: AuthStatus;
   signInWithEmail: (username: string, password: string) => Promise<void>;
   signUpWithEmail: (
      email: string,
      password: string,
      donorId: string,
      internalDonorId: string,
      firstName: string,
      lastName: string,
      dateOfBirth: string,
      phoneNumber: string
   ) => Promise<ISignUpResult>;
   signOut: () => void;
   signOutForTimeout: () => void;
   verifyCode: (username: string, code: string) => Promise<void>;
   getSession: (cognitoUsername: string) => Promise<CognitoUserSession>;
   sendCode: (username: string) => Promise<void>;
   forgotPassword: (username: string) => Promise<void>;
   confirmPassword: (
      username: string,
      code: string,
      password: string,
   ) => Promise<void>;
   resendCode: (username: string) => Promise<void>;
   changePassword: (
      oldPassword: string,
      newPassword: string,
   ) => Promise<string>;
   getAttributes: () => Promise<CognitoUserAttribute[] | undefined>;
   setAttribute: (attr: CognitoUserAttribute) => Promise<string | undefined>;
   verifyDonorIdForEmail(email: string, donorId: string): Promise<boolean>;
   handleUnverifiedEmail(email: string, donorId: string): Promise<void>;
}

const defaultState: IAuthContext = {
   signInWithEmail(username: string, password: string): Promise<void> {
      throw new Error("Not Instantiated");
   },
   signUpWithEmail(
      email: string,
      password: string,
      donorId: string,
      internalDonorId: string,
      firstName: string,
      lastName: string
   ): Promise<ISignUpResult> {
      throw new Error("Not Instantiated");
   },
   changePassword(oldPassword: string, newPassword: string): Promise<string> {
      throw new Error("Not Instantiated");
   },
   confirmPassword(
      username: string,
      code: string,
      password: string
   ): Promise<void> {
      throw new Error("Not Instantiated");
   },
   forgotPassword(username: string): Promise<void> {
      throw new Error("Not Instantiated");
   },
   getAttributes(): Promise<CognitoUserAttribute[] | undefined> {
      throw new Error("Not Instantiated");
   },
   getSession(cognitoUsername: string): Promise<CognitoUserSession> {
      throw new Error("Not Instantiated");
   },
   resendCode(username: string): Promise<void> {
      throw new Error("Not Instantiated");
   },
   sendCode(username: string): Promise<void> {
      throw new Error("Not Instantiated");
   },
   setAttribute(attr: CognitoUserAttribute): Promise<string | undefined> {
      throw new Error("Not Instantiated");
   },
   verifyCode(username: string, code: string): Promise<void> {
      throw new Error("Not Instantiated");
   },
   sessionInfo: {},
   authStatus: AuthStatus.Loading,
   userDonorId: "",
   externalDonorId: "",
   signOut: () => {
      throw new Error("Not Instantiated");
   },
   signOutForTimeout: () => {
      throw new Error("Not Instantiated");
   },
   verifyDonorIdForEmail: function (email: string, donorId: string): Promise<boolean> {
      throw new Error("Function not implemented.");
   },
   handleUnverifiedEmail: function (email: string, donorId: string): Promise<void> {
      throw new Error("Function not implemented.");
   }
};

type Props = {
   children?: React.ReactNode;
};

export const AuthContext = React.createContext(defaultState);

// export const AuthIsSignedIn = ({ children }: Props) => {
//    const { authStatus }: IAuthContext = useContext(AuthContext);
//
//    return <>{authStatus === AuthStatus.SignedIn ? children : null}</>;
// };

const AuthProvider = ({ children }: Props) => {
   const [authStatus, setAuthStatus] = useState(AuthStatus.Loading);
   const [userDonorId, setUserDonorId] = useState<string>("");
   const [externalDonorId, setExternalDonorId] = useState<string>("");
   const [sessionInfo, setSessionInfo] = useState<ISessionInfo | {}>({});
   const [attrInfo, setAttrInfo] = useState<[]>([]);
   const authServiceRef = useRef<ICognitoService<any>>(getCognitoService());
   const authService: ICognitoService<any> = authServiceRef.current;
   const storedCognitoUsername: string | null =
       sessionStorage.getItem("cognitoUsername");
   const settingSession = async (
      session: CognitoUserSession,
   ): Promise<void> => {
      const sessionAccessToken: CognitoAccessToken = session.getAccessToken();
      const rollingExpiration: number = sessionAccessToken.getExpiration();
      const expirationTimeout: number = rollingExpiration- sessionAccessToken.getIssuedAt();
      const jwtToken: string = sessionAccessToken.getJwtToken();
      const cognitoUsername: string =
         session.getAccessToken().payload?.username;
      setSessionInfo({
         accessToken: jwtToken,
         refreshToken: session.getRefreshToken().getToken(),
         username: cognitoUsername,
      });
      sessionStorage.setItem("rollingExpiration", `${rollingExpiration}`);
      sessionStorage.setItem("expirationTimeout", `${expirationTimeout}`)
      sessionStorage.setItem("cognitoUsername", cognitoUsername);
      const response: CognitoUserAttribute[] | undefined =
         await getAttributes();
      if (response && Array.isArray(response)) {
         const donorId: string | undefined = response.find(
             eachAttribute => eachAttribute.Name === "custom:donorId",
         )?.Value;

         const internalDonorId: string | undefined = response.find(
             eachAttribute => eachAttribute.Name === "custom:internalDonorId",
         )?.Value;

         if (internalDonorId && donorId) {
            setUserDonorId(internalDonorId);
            setExternalDonorId(donorId)
            setAuthStatus(AuthStatus.SignedIn);
         } else if (donorId) {
            const externalDonorPromise: Promise<IExternalDonorDTO> = getDonorDataServiceInstance().getExternalDonor(donorId);

            externalDonorPromise.then((externalDonorPromise) => {
               setUserDonorId(externalDonorPromise.donorId);
               setExternalDonorId(externalDonorPromise.externalDonorId)
               setAuthStatus(AuthStatus.SignedIn);
               setAttribute(new CognitoUserAttribute({
                  Name: "custom:internalDonorId",
                  Value: `${externalDonorPromise.donorId}`,
               }))
            });
         }
      }
   };

   async function getSessionInfo(cognitoUsername: string): Promise<void> {
      try {
         const session: CognitoUserSession = await authService.getSession(
            cognitoUsername,
         );
         await settingSession(session);
      } catch (err) {
         setAuthStatus(AuthStatus.SignedOut);
      }
   }

   useEffect(() => {
      if (storedCognitoUsername) {
         getSessionInfo(storedCognitoUsername);
      } else {
         setAuthStatus(AuthStatus.SignedOut);
      }
   }, []);

   if (authStatus === AuthStatus.Loading) {
      return null;
   }

   async function signInWithEmail(
      username: string,
      password: string,
   ): Promise<void> {
      try {
         const response: ICognitoSignInResponse =
            await authService.signInWithEmail(username, password);
         if (response?.session) {
            await settingSession(response.session);
         }
      } catch (err) {
         setAuthStatus(AuthStatus.SignedOut);
         throw err;
      }
   }

   async function signUpWithEmail(
      email: string,
      password: string,
      donorId: string,
      internalDonorId: string,
      firstName: string,
      lastName: string,
      dateOfBirth: string,
      phoneNumber: string
   ): Promise<ISignUpResult> {
      const validationRequest: IDonorValidationRequest = {
         firstName: firstName,
         lastName: lastName,
         emailAddress: email,
         donorId: parseInt(donorId), // This should be the external donor ID.
         dateOfBirth: dateOfBirth,
         phoneNumber: phoneNumber
      };

      // Validate
      try {
         const validatedInternalDonorId: string =
            await getDonorDataServiceInstance().validateDonor(
               validationRequest,
            );
         if (validatedInternalDonorId) {
            // Create user
            const response: ISignUpResult = await authService.signUp({
               usernameField: "email",
               password,
               email,
               donorId,
               internalDonorId: validatedInternalDonorId
            });

            return response;
         } else {
            throw new Error("Donor Data is not valid");
         }
      } catch (err) {
         throw err;
      }
   }

   function signOut(): void {
      baseSignOut();

      // Reload the page upon logging out to refresh the API context/state (SCREDP-31)
      window.location.reload();
   }

   function signOutForTimeout(): void {
      baseSignOut();
   }

   function baseSignOut(): void {
      authService.signOut();
      setUserDonorId("");
      setExternalDonorId("");
      authService.removeStorageItems();
      setAuthStatus(AuthStatus.SignedOut);
   }

   async function verifyCode(username: string, code: string): Promise<void> {
      try {
         await authService.verifyCode(username, code);
      } catch (err) {
         throw err;
      }
   }

   async function getSession(
      cognitoUsername: string,
   ): Promise<CognitoUserSession> {
      try {
         const session: CognitoUserSession = await authService.getSession(
            cognitoUsername,
         );
         return session;
      } catch (err) {
         throw err;
      }
   }

   async function getAttributes(): Promise<CognitoUserAttribute[] | undefined> {
      try {
         const attr: CognitoUserAttribute[] | undefined =
            await authService.getAttributes();
         return attr;
      } catch (err) {
         throw err;
      }
   }

   async function setAttribute(
      attr: CognitoUserAttribute,
   ): Promise<string | undefined> {
      try {
         const res: string | undefined = await authService.setAttribute(attr);
         return res;
      } catch (err) {
         throw err;
      }
   }

   async function sendCode(username: string): Promise<void> {
      try {
         await authService.sendCode(username);
      } catch (err) {
         throw err;
      }
   }

   async function forgotPassword(username: string): Promise<void> {
      try {
         await authService.forgotPassword(username);
      } catch (err) {
         throw err;
      }
   }

   async function resendCode(username: string): Promise<void> {
      try {
         await authService.resendCode(username);
      } catch (err) {
         throw err;
      }
   }

   async function confirmPassword(
      username: string,
      code: string,
      password: string,
   ): Promise<void> {
      try {
         await authService.confirmPassword(username, code, password);
      } catch (err) {
         throw err;
      }
   }

   async function changePassword(
      oldPassword: string,
      newPassword: string,
   ): Promise<"SUCCESS"> {
      try {
         const response: "SUCCESS" = await authService.changePassword(
            oldPassword,
            newPassword,
         );
         return response;
      } catch (err) {
         throw err;
      }
   }

   // SCREDP-68: verify donor id for the user's email if the email is unverified
   async function verifyDonorIdForEmail(email: string, donorId: string): Promise<boolean> {
      const validationRequest: IDonorIdEmailVerificationRequest = {
         email: email,
         donorId: donorId,
      };
      return await getDonorDataServiceInstance().verifyDonorIdForEmail(
         validationRequest,
      );
   }
  
   async function handleUnverifiedEmail(email: string, donorId: string): Promise<void> {
      try {
         const isValid = await verifyDonorIdForEmail(email, donorId);
         if (isValid) {
            await authService.resendCode(email);
         } else {
            throw new Error(`We could not match the details you have provided to an existing account.`);
         }
      } catch (err) {
         throw err;
      }
   }
  

   const state: IAuthContext = {
      authStatus,
      userDonorId,
      externalDonorId,
      sessionInfo,
      attrInfo,
      signUpWithEmail,
      signInWithEmail,
      signOut,
      signOutForTimeout,
      verifyCode,
      getSession,
      sendCode,
      forgotPassword,
      resendCode,
      confirmPassword,
      changePassword,
      getAttributes,
      setAttribute,
      verifyDonorIdForEmail,
      handleUnverifiedEmail,
   };

   return <AuthContext.Provider value={state}>{children}</AuthContext.Provider>;
};

export default AuthProvider;
